import { Button } from '@hopin-team/ui-button';
import { MenuContent, MenuSelectLink } from '@hopin-team/ui-menu';
import React, { useRef, useState } from 'react';
import { useLayer } from 'react-laag';

import { AddToCalendarMenu, StyledButticon } from './AddToCalendar.styles';
import createCalendarLinks from './create-calendar-links';
import type { AddToCalendarButtonProps } from './types';

export const AddToCalendarButton = ({
  label,
  eventDetails,
  shouldOverflow = false,
  asIcon,
  onAddedToCalendar,
  icon,
  size,
  isOutlined = true,
}: AddToCalendarButtonProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const wrapperRef = useRef(null);

  const { renderLayer, triggerProps, layerProps } = useLayer({
    isOpen: isMenuOpen,
    container: wrapperRef?.current || undefined,
    onOutsideClick: () => setIsMenuOpen(false),
    overflowContainer: shouldOverflow,
    auto: true,
    placement: 'bottom-start',
    triggerOffset: 0,
    containerOffset: 0,
  });

  const links = createCalendarLinks(eventDetails);

  const toggleMenu = (event: React.KeyboardEvent | React.MouseEvent) => {
    event.stopPropagation();
    setIsMenuOpen(isMenuOpen => !isMenuOpen);
  };

  const onScheduleItemAddedToCalendar = (
    e: React.KeyboardEvent | React.MouseEvent,
  ) => {
    e.stopPropagation();
    onAddedToCalendar?.();
  };

  return (
    <div ref={wrapperRef}>
      {asIcon ? (
        <StyledButticon
          icon={icon || 'calendar-add'}
          onClick={toggleMenu}
          isOutlined={isOutlined}
          size={size || 'tiny'}
          title={label}
          data-testid="add-to-calendar-icon-button"
          {...triggerProps}
        />
      ) : (
        <Button
          pattern="secondary"
          size={size || 'small'}
          leadingIcon={icon || 'calendar-add'}
          isOutlined={isOutlined}
          onClick={toggleMenu}
          px={2}
          {...triggerProps}
        >
          {label}
        </Button>
      )}

      {isMenuOpen &&
        renderLayer(
          <AddToCalendarMenu
            isInline={true}
            isShowing={isMenuOpen}
            onClose={() => setIsMenuOpen(false)}
            {...layerProps}
          >
            <MenuContent p={1}>
              {links.map(link => (
                <div
                  key={link.key}
                  role="presentation"
                  onKeyDown={onScheduleItemAddedToCalendar}
                  onClick={onScheduleItemAddedToCalendar}
                >
                  <MenuSelectLink
                    key={link.key}
                    label={link.title}
                    leadingIcon={link.icon}
                    itemUrl={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={link.download}
                    py={1}
                    pl={2}
                    pr={4}
                  />
                </div>
              ))}
            </MenuContent>
          </AddToCalendarMenu>,
        )}
    </div>
  );
};
